import { Ref, forwardRef } from "react"
import * as yup from "yup"
import { Form, FormikProps } from "formik"
import { Formik } from "../../../../../../shared/components/forms/SchemaFormik"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { ApiRoomDisplay, CreateDisplayFormValues, DisplayTypes } from "../deviceTypes"
import { CreateUpdateDeviceForm } from "./CreateUpdateDeviceForm"

type CreateUpdateDeviceFormProps = Readonly<{
    initialValues?: CreateDisplayFormValues | null
    isEditMode?: boolean
    onSubmit: (formData: { device: ApiRoomDisplay }) => void
}>
type FormikRef = Ref<FormikProps<CreateDisplayFormValues>>

const defaultInitialValues: CreateDisplayFormValues = {
    type: DisplayTypes.ROOM_DISPLAY,
    room: undefined,
    deviceNumber: "",
    name: "",
    templateId: undefined,
    isActive: true,
    serialNumber: "",
}

export const CreateUpdateDeviceFormNew = forwardRef(
    ({ initialValues, isEditMode, onSubmit }: CreateUpdateDeviceFormProps, ref: FormikRef) => {
        const { l10n } = useAppLocalization()

        const validationSchema = yup.object({
            type: yup.mixed<DisplayTypes>().oneOf(Object.values(DisplayTypes)),
            room: yup.mixed().when("type", {
                is: (val: DisplayTypes) => val !== DisplayTypes.MEETING_DISPLAY,
                then: () =>
                    yup
                        .object({
                            id: yup.string(),
                            name: yup.string(),
                        })
                        .test(
                            "room-has-id",
                            l10n.getString("resource-room-create-required-field-empty"),
                            (value) => !!value?.id,
                        )
                        .required(l10n.getString("resource-room-create-required-field-empty")),
                otherwise: () => yup.mixed().strip(),
            }),
            deviceNumber: yup.string().when("type", {
                is: DisplayTypes.MEETING_DISPLAY,
                then: (schema) => schema.optional(),
                otherwise: (schema) => schema.required(l10n.getString("resource-room-create-required-field-empty")),
            }),
            floor: yup.number().when("type", {
                is: DisplayTypes.MEETING_DISPLAY,
                then: (schema) => schema.optional(),
                otherwise: (schema) => schema.strip(),
            }),
            name: yup.string().required(l10n.getString("resource-room-create-required-field-empty")),
            templateId: yup.number().required(l10n.getString("resource-room-create-required-field-empty")),
            active: yup.boolean(),
            serialNumber: yup.string(),
        })

        function handleSubmit(data: CreateDisplayFormValues) {
            const formDevice = {
                device: {
                    type: data.type,
                    name: data.name,
                    isActive: data.isActive,
                    serialNumber: data.serialNumber,
                    deviceNumber: data.deviceNumber,
                    ...(data.floor && {
                        meetingDisplayDetails: {
                            floor: data.floor,
                        },
                    }),
                    template: {
                        id: data.templateId,
                    },
                    ...(data.room?.id && {
                        serviceCenter: {
                            id: data.room?.id,
                        },
                    }),
                },
            }
            onSubmit(formDevice)
        }

        return (
            <>
                <Formik<CreateDisplayFormValues>
                    enableReinitialize
                    initialValues={initialValues ?? defaultInitialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    innerRef={ref}
                >
                    <Form>
                        <CreateUpdateDeviceForm isEditMode={isEditMode} />
                    </Form>
                </Formik>
            </>
        )
    },
)
